import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CryptoJS from 'crypto-js';
import './LuxuryCasinoLogin.css';
import api from '../api';
import { FaCrown, FaStar, FaHeart } from 'react-icons/fa';
import { GiDiceSixFacesSix } from 'react-icons/gi';
import { useCasinoAudio } from './CasinoAudio';

const API = process.env.REACT_APP_API;
const API_V2 = 'https://phoenix-bingo-2.onrender.com/api';

// Floating Casino Element Component
const FloatingElement = ({ icon: Icon, delay = 0, duration = 3 }) => (
  <div 
    className="floating-casino-element" 
    style={{ 
      animationDelay: `${delay}s`,
      animationDuration: `${duration}s` 
    }}
  >
    <Icon />
  </div>
);

// Premium Bingo Ball Component
const LuxuryBingoBall = ({ number, delay = 0 }) => (
  <div 
    className="luxury-bingo-ball" 
    style={{ animationDelay: `${delay}s` }}
  >
    <div className="ball-inner">
      <span className="ball-number">{number}</span>
    </div>
  </div>
);

// Particle Component
const GoldParticle = ({ style }) => (
  <div className="gold-particle" style={style}></div>
);

// April 1 Countdown Component
const AprilCountdown = () => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const countdown = useMemo(() => {
    const target = new Date('2026-04-01T00:00:00');
    const diff = Math.max(0, target - now);
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    const seconds = Math.floor((diff / 1000) % 60);
    return { days, hours, minutes, seconds, expired: diff === 0 };
  }, [now]);

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div className="april-countdown">
      <div className="april-countdown-badge">April 1</div>
      {countdown.expired ? (
        <p className="april-countdown-expired">የቢንጎ አዲስ ዘመን ደርሷል!</p>
      ) : (
        <>
          <p className="april-countdown-tagline">
            አስደናቂ ለውጦች እየመጡ ነው
          </p>
          <div className="april-countdown-grid">
            {[
              { value: countdown.days, label: 'ቀናት' },
              { value: countdown.hours, label: 'ሰዓት' },
              { value: countdown.minutes, label: 'ደቂቃ' },
              { value: countdown.seconds, label: 'ሴኮንድ' },
            ].map(({ value, label }) => (
              <div key={label} className="april-countdown-item">
                <div className="april-countdown-value">{pad(value)}</div>
                <div className="april-countdown-label">{label}</div>
              </div>
            ))}
          </div>
          <p className="april-countdown-date">
            April 1, 2026 &mdash; አዲስ የቢንጎ ተሞክሮ
          </p>
        </>
      )}
    </div>
  );
};

// Main Luxury Casino Login Component
function LuxuryCasinoLogin({
  onSubmit,
  username,
  setUsername,
  password,
  setPassword,
  apiVersion,
  setApiVersion,
  isLoading,
  showVersionChoice
}) {
  const [balls, setBalls] = useState([]);
  const [particles, setParticles] = useState([]);
  const [audioInitialized, setAudioInitialized] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem('rememberMe') === 'true');

  // Load saved credentials on mount
  useEffect(() => {
    if (window.electronAPI?.isElectron && localStorage.getItem('rememberMe') === 'true') {
      const savedUser = localStorage.getItem('savedUsername');
      const savedPass = localStorage.getItem('savedPassword');
      if (savedUser) setUsername(savedUser);
      if (savedPass) setPassword(savedPass);
    }
  }, [setUsername, setPassword]);
  
  // Initialize casino audio system
  const { 
    initAudio, 
    playCardFlip, 
    playChipClick, 
    playGoldChime, 
    playSuccess, 
    playError,
    getState 
  } = useCasinoAudio();

  // Initialize audio on first user interaction
  const handleFirstInteraction = () => {
    if (!audioInitialized) {
      initAudio();
      setAudioInitialized(true);
      playGoldChime(); // Welcome chime
    }
  };

  // Generate random bingo balls
  useEffect(() => {
    const newBalls = Array.from({ length: 5 }, () => Math.floor(Math.random() * 75) + 1);
    setBalls(newBalls);
  }, []);

  // Generate gold particles (reduced for performance)
  useEffect(() => {
    const newParticles = Array.from({ length: 15 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      animationDelay: Math.random() * 10,
      size: Math.random() * 3 + 1
    }));
    setParticles(newParticles);
  }, []);

  const casinoIcons = [
    { icon: FaStar, delay: 0, duration: 4 },
    { icon: FaStar, delay: 1, duration: 5 },
    { icon: FaHeart, delay: 2, duration: 3.5 },
    { icon: FaStar, delay: 3, duration: 4.5 },
    { icon: FaStar, delay: 0.5, duration: 6 },
    { icon: GiDiceSixFacesSix, delay: 2.5, duration: 4 }
  ];

  // Enhanced form submission with audio feedback
  const handleFormSubmit = async (e) => {
    handleFirstInteraction(); // Initialize audio
    playCardFlip(); // Sound effect for form submission

    // Save credentials if remember me is checked (Electron only)
    if (window.electronAPI?.isElectron && rememberMe) {
      localStorage.setItem('rememberMe', 'true');
      localStorage.setItem('savedUsername', username);
      localStorage.setItem('savedPassword', password);
    } else {
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('savedUsername');
      localStorage.removeItem('savedPassword');
    }

    try {
      await onSubmit(e);
      playSuccess(); // Success sound on login
    } catch (error) {
      playError(); // Error sound on failure
      throw error;
    }
  };

  // Input interaction sounds
  const handleInputFocus = () => {
    handleFirstInteraction();
    playChipClick();
  };

  const handleRadioChange = (value) => {
    handleFirstInteraction();
    playChipClick();
    setApiVersion(value);
  };

  return (
    <div
      className="luxury-casino-container"
      onClick={handleFirstInteraction}
    >
      {/* Animated Background Particles */}
      <div className="particle-system">
        {particles.map(particle => (
          <GoldParticle
            key={particle.id}
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              animationDelay: `${particle.animationDelay}s`,
              width: `${particle.size}px`,
              height: `${particle.size}px`
            }}
          />
        ))}
      </div>

      {/* Floating Casino Elements */}
      <div className="floating-elements">
        {casinoIcons.map((item, index) => (
          <FloatingElement
            key={index}
            icon={item.icon}
            delay={item.delay}
            duration={item.duration}
          />
        ))}
      </div>

      {/* Main Login Content */}
      <div className="casino-login-content">

        {/* April 1 Countdown */}
        <AprilCountdown />

        {/* Premium Login Card */}
        <div className="casino-login-card">
          {/* Royal Header */}
          <div className="royal-header">
            <div className="royal-title">
              <h1 className="casino-title">
                <span className="uno-text">UNO</span>
                <span className="bingo-text">BINGO</span>
              </h1>
            </div>
          </div>

          {/* Premium Form */}
          <form onSubmit={handleFormSubmit} className="luxury-form">
            <div className="luxury-form-group">
              <label htmlFor="username" className="luxury-label">
                <FaStar className="label-icon" />
                Username
              </label>
              <div className="luxury-input-wrapper">
                <input
                  type="text"
                  id="username"
                  className="luxury-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  onFocus={handleInputFocus}
                  placeholder="Enter your username"
                  required
                />
                <div className="input-glow"></div>
              </div>
            </div>

            <div className="luxury-form-group">
              <label htmlFor="password" className="luxury-label">
                <FaStar className="label-icon" />
                Password
              </label>
              <div className="luxury-input-wrapper">
                <input
                  type="password"
                  id="password"
                  className="luxury-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={handleInputFocus}
                  placeholder="Enter your password"
                  required
                />
                <div className="input-glow"></div>
              </div>
            </div>

            {showVersionChoice && (
              <div className="luxury-form-group version-selector">
                <label className="luxury-label">
                  <FaStar className="label-icon" />
                  Cartella Version
                </label>
                <div className="luxury-radio-group">
                  <label className="luxury-radio-label">
                    <input
                      type="radio"
                      value="1"
                      checked={apiVersion === '1'}
                      onChange={(e) => handleRadioChange(e.target.value)}
                      className="luxury-radio"
                    />
                    <span className="radio-custom"></span>
                    <span className="radio-text">Cartella 1</span>
                  </label>
                  <label className="luxury-radio-label">
                    <input
                      type="radio"
                      value="2"
                      checked={apiVersion === '2'}
                      onChange={(e) => handleRadioChange(e.target.value)}
                      className="luxury-radio"
                    />
                    <span className="radio-custom"></span>
                    <span className="radio-text">Cartella 2</span>
                  </label>
                </div>
              </div>
            )}

            {/* Remember Me — Electron only */}
            {window.electronAPI?.isElectron && (
              <div className="luxury-form-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{ accentColor: '#D4AF37', width: 16, height: 16, cursor: 'pointer' }}
                />
                <label htmlFor="rememberMe" style={{ color: '#D4AF37', fontSize: 13, cursor: 'pointer', userSelect: 'none' }}>
                  Remember me
                </label>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="luxury-submit-btn"
            >
              <span className="btn-content">
                {isLoading ? (
                  <>
                    <div className="loading-spinner"></div>
                    Logging in...
                  </>
                ) : (
                  <>
                    <FaCrown className="btn-icon" />
                    Log In
                  </>
                )}
              </span>
              <div className="btn-shimmer"></div>
            </button>
          </form>

          {/* Decorative Border */}
          <div className="card-border">
            <div className="border-corner top-left"></div>
            <div className="border-corner top-right"></div>
            <div className="border-corner bottom-left"></div>
            <div className="border-corner bottom-right"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Authentication function (preserved from original)
async function login(username, password, apiVersion) {
  const baseUrl = apiVersion === '2' ? API_V2 : API;

  if (!baseUrl) {
    throw new Error('API URL is not defined. Please check your environment variables.');
  }

  try {
    const data = await api.post(`${baseUrl}/admin/login`, {
      username,
      password
    });

    if (data.token) {
      const hashedApiVersion = CryptoJS.SHA256(apiVersion).toString();
      localStorage.setItem('token', data.token);
      localStorage.setItem('apiVersion', hashedApiVersion);
      localStorage.setItem('username', data.username);
      localStorage.setItem('useLocalCards', data.useLocalCards ? 'true' : 'false');
      if (data.adminId) {
        localStorage.setItem('adminId', data.adminId);
      }
      return data;
    } else {
      throw new Error('Unauthorized');
    }
  } catch (err) {
    console.error('Login error:', err);
    throw err;
  }
}

// Main Login Component
function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [apiVersion, setApiVersion] = useState('1');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showVersionChoice, setShowVersionChoice] = useState(false);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const data = await login(username, password, apiVersion);
      if (data.adminId) {
        localStorage.setItem('adminId', data.adminId);
      }
      
      // Add success toast with luxury styling
      toast.success('ወደ Uno ቢንጎ እንኳን በደህና መጡ !', {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: 'luxury-toast-success'
      });
      
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1000);
    } catch (err) {
      toast.error(`Access Denied: ${err.message} 🚫`, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: 'luxury-toast-error'
      });
      console.error('Login error:', err);
    } finally {
      setIsLoading(false);
    }
  };


  return (
      <div className="luxury-casino-wrapper">
        <LuxuryCasinoLogin
          onSubmit={handleSubmit}
          username={username}
          setUsername={setUsername}
          password={password}
          setPassword={setPassword}
          apiVersion={apiVersion}
          setApiVersion={setApiVersion}
          isLoading={isLoading}
          showVersionChoice={showVersionChoice}
        />
        <ToastContainer 
          position="top-center"
          theme="dark"
          toastClassName="luxury-toast"
        />
      </div>
    );
}

export default Login;